<template>
    <ErrorModal />
    <Toast />
    <ConfirmDialog group="templating">
        <template #message="slotProps">
            <div class="flex flex-column align-items-center w-full gap-3 surface-border">
                <p class="white-space">{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
    <template v-if="isAuthenticated">
        <div class="ent-items">

            <div class="content primary auth">
                <router-link :to="{ name: 'enterprise', params: { url: '/enterprise' }}">
                    <Card>
                        <template #content>
                            <div class="item">
                                <div class="icon"><img src="../assets/img/icon-enterprisereports.svg" alt="Enterprise Reports"></div>
                                <div class="content">
                                    <div class="title"><h2>Enterprise Reports</h2></div>
                                    <div class="prose"><p>Reporting for all of your stores' core KPIs<br />and metrics.</p></div>
                                </div>
                            </div>
                        </template>
                    </Card>
                </router-link>
            </div>

            <div class="content secondary auth">
                <Card>
                    <template #content>
                        <div class="item">
                            <div class="icon"><img src="../assets/img/icon-storelogin.svg" alt="Store Login"></div>
                            <div class="content">
                                <div class="title"><h2>Store Login</h2></div>
                                <div class="prose">
                                    <p class="lead text-muted"><strong>Store:</strong>
                                        <Dropdown :disabled="loading" v-model="selectedEnterpriseStore" filter :options="enterprisestores" optionLabel="StoreName" :placeholder="selectedEnterpriseStore ? selectedEnterpriseStore.StoreName : 'Select an Enterprise store'" class="storeChange" @change="updateEnterpriseStore" /> <Button label="Login" class="logout p-button-rounded btn-secondary sml" @click="storeLogin" />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>

        </div><!-- .ent-items -->
        
    </template>

    <EnterpriseStoreError v-if="isAuthenticated && enterprisestoreerror" />
</template>

<script lang="ts" setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { ref, onMounted } from "vue";
import ApiService from "@/service/api.service";
import ErrorModal from '../views/ErrorModal.vue';
import { openErrorModal } from '@/composables/useErrorLog';
import EnterpriseStoreError from "./EnterpriseStoreError.vue";
import { useStore } from "vuex";
import { useToast } from 'primevue/usetoast';
import { useConfirm } from "primevue/useconfirm";
import { MutationTypes } from "../store/mutations";

const confirm = useConfirm();
const enterprisestoreerror = ref(false);
const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
const store = useStore();
const loading = ref(false);
const enterprisestores = ref([]);
const selectedEnterpriseStore = ref();
const storeid = ref('');
const toast = useToast();

onMounted(async() => {
    const enterpriseid = store.state.enterpriseid;
    const accessToken = await getAccessTokenSilently();
    await ApiService.getEnterpriseStores(accessToken, enterpriseid);
    storeid.value = store.state.storeid;
    enterprisestores.value = store.state.enterprisestores;
    selectedEnterpriseStore.value = enterprisestores.value.find(item => item.StoreId == storeid.value);
});

const updateEnterpriseStore = () => {
    confirm.require({
        group: 'templating',
        header: 'Please confirm store change',
        message: 'Confirm the availability of the store in the Available Enterprise Store list. \nDo you wish to change to the selected store?',
        icon: 'pi pi-exclamation-circle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        rejectClass: 'p-button-outlined p-button-sm',
        acceptClass: 'p-button-sm btn-primary',
        rejectLabel: 'Cancel',
        acceptLabel: 'Continue',
        accept: async () => {
          loading.value = true;
          const { StoreId, StoreName } = selectedEnterpriseStore.value;
          const userid = user.value.sub;
          const userdata = { storeid: StoreId };
          try {
            const accessToken = await getAccessTokenSilently();
            const response = await fetch(process.env.VUE_APP_NODEJS_BASE_URL + "enterprises/users/" + userid,  {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(userdata),
            });

            const data = await response.json();
            loading.value = false;
            if (response.status > 400) {
              throw new Error(JSON.stringify(data.error));
            }
            toast.add({ severity: 'success', summary: 'Changed successfully', detail: `You can now view ${StoreName}.`, life: 3000 });
            localStorage.setItem('storeid', StoreId);
            store.commit(MutationTypes.SET_STOREID, StoreId);
            
            return data.message;
          } catch (e) {
            openErrorModal(e.message);
          }
        },
        reject: () => {
          toast.add({ severity: 'error', summary: 'Rejected', detail: 'Enterprise store change cancelled.', life: 3000 });
        }
    });
};

const storeLogin = () => {
    store.commit(MutationTypes.SET_ISSTORELOGIN, true);
}
</script>
